export function normalizeOrgTreeNode(node) {
  if (!node || typeof node !== 'object') return null;
  const name = String(node.name || node.title || '').trim();
  const childrenRaw = Array.isArray(node.children) ? node.children : [];
  const children = childrenRaw.map(normalizeOrgTreeNode).filter(Boolean);
  if (!name && children.length === 0) return null;
  return { name, children };
}

export async function loadUserManagementOrgTree(fetchImpl, token) {
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetchImpl('/api/audit-core/org-chart', { headers, credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      const normalized = normalizeOrgTreeNode(data);
      if (normalized) return normalized;
    }
  } catch (_) {}

  const fallback = await fetchImpl('/org-structure.json', { credentials: 'include' });
  if (!fallback.ok) throw new Error('load org-structure failed');
  const data = await fallback.json();
  const normalized = normalizeOrgTreeNode(data);
  return normalized || null;
}

export function collectFirstLevelDeptOptions(orgTree) {
  const list = Array.isArray(orgTree?.children) ? orgTree.children : [];
  return list.filter((node) => node?.name).map((node) => node.name);
}

export function collectAllDeptOptions(orgTree) {
  const out = [];
  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    const name = String(node.name || '').trim();
    if (name && name !== 'ROOT') out.push(name);
    const children = Array.isArray(node.children) ? node.children : [];
    children.forEach(walk);
  };
  walk(orgTree);
  return Array.from(new Set(out));
}

export function buildOrgDeptOptionState(orgTree) {
  return {
    firstLevelOptions: collectFirstLevelDeptOptions(orgTree),
    allOrgDeptOptions: collectAllDeptOptions(orgTree),
  };
}

export function getSecondLevelOptionsFromTree(orgTree, deptL1) {
  const first = String(deptL1 || '').trim();
  if (!first) return [];
  const firstNode = (orgTree?.children || []).find((node) => node?.name === first);
  const children = Array.isArray(firstNode?.children) ? firstNode.children : [];
  return children.filter((node) => node?.name).map((node) => node.name);
}

export function getThirdLevelOptionsFromTree(orgTree, deptL1, deptL2) {
  const first = String(deptL1 || '').trim();
  const second = String(deptL2 || '').trim();
  if (!first || !second) return [];
  const firstNode = (orgTree?.children || []).find((node) => node?.name === first);
  const secondNode = (firstNode?.children || []).find((node) => node?.name === second);
  const children = Array.isArray(secondNode?.children) ? secondNode.children : [];
  return children.filter((node) => node?.name).map((node) => node.name);
}

export function buildDeptCascadeOptions(orgTree, path = {}) {
  const deptL1 = path?.deptL1 || '';
  const deptL2 = path?.deptL2 || '';
  return {
    secondLevelOptions: getSecondLevelOptionsFromTree(orgTree, deptL1),
    thirdLevelOptions: getThirdLevelOptionsFromTree(orgTree, deptL1, deptL2),
    isSecondLevelDisabled: !deptL1,
    isThirdLevelDisabled: !deptL2,
  };
}

export function findDeptPathByName(orgTree, targetName) {
  const target = String(targetName || '').trim();
  if (!target || !orgTree) return [];
  const walk = (node, path = []) => {
    if (!node || typeof node !== 'object') return null;
    const nodeName = String(node.name || '').trim();
    const nextPath = nodeName && nodeName !== 'ROOT' ? [...path, nodeName] : path;
    if (nodeName === target) return nextPath;
    const children = Array.isArray(node.children) ? node.children : [];
    for (const child of children) {
      const found = walk(child, nextPath);
      if (found) return found;
    }
    return null;
  };
  return walk(orgTree, []) || [];
}
